import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  SuggestionPopoverContent,
  type SuggestionPopoverVariant,
} from "../../SuggestionPopover";
import type { SuggestionHighlightAttrs } from "../types";
import "./SuggestionBubbleMenu.css";

const SUGGESTION_BUBBLE_MENU_KEY = "suggestion-bubble-menu";

interface SuggestionBubbleMenuProps {
  editor: Editor | null;
  /**
   * Layout variant for the suggestion popover content.
   * - "default": Vertical layout with full details (reason, explanation, quote, confidence)
   * - "minimal": Horizontal compact layout with just reason and actions
   * Defaults to "default".
   */
  variant?: SuggestionPopoverVariant;
  onAccept: (suggestionId: string) => void;
  onReject: (suggestionId: string) => void;
  onNavigateToSegment?: (segmentId: string) => void;
}

/**
 * BubbleMenu that appears when cursor is inside a suggestion highlight.
 *
 * Shows suggestion details (reason, explanation, confidence) and Accept/Reject buttons.
 * Accept deletes the highlighted text range; Reject dismisses the suggestion.
 *
 * Design decisions:
 * - Uses TipTap BubbleMenu with shouldShow configured for suggestion marks
 * - Shows when cursor is inside a suggestionHighlight mark (user clicks highlighted text)
 * - Uses getMarkRange() to get mark positions for delete (no offset mapping needed)
 * - Accept button is disabled for invalid suggestions (content changed)
 */
export const SuggestionBubbleMenu: React.FC<SuggestionBubbleMenuProps> = ({
  editor,
  variant = "default",
  onAccept,
  onReject,
  onNavigateToSegment,
}) => {
  // Force re-render when editor selection changes
  // BubbleMenu shows/hides internally but doesn't trigger React re-renders
  const [activeSuggestion, setActiveSuggestion] =
    useState<SuggestionHighlightAttrs | null>(null);
  const bubbleMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bubbleMenuRef.current) {
      bubbleMenuRef.current.style.zIndex = "var(--indigo-z-ant-popover)";
    }
  }, []);

  // The plugin first measures the menu before React renders its suggestion.
  // Reposition with the committed content so flip uses the actual menu size.
  useEffect(() => {
    if (!editor || !activeSuggestion) return;
    editor.view.dispatch(
      editor.state.tr.setMeta(SUGGESTION_BUBBLE_MENU_KEY, "updatePosition"),
    );
  }, [editor, activeSuggestion]);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      // Check if cursor is inside a suggestion
      const isActive = editor.isActive("suggestionHighlight");
      if (isActive) {
        // Pull the specific attributes for the mark at the current cursor
        setActiveSuggestion(
          editor.getAttributes(
            "suggestionHighlight",
          ) as SuggestionHighlightAttrs,
        );
      } else {
        setActiveSuggestion(null);
      }
    };

    // Listen for any cursor movement or click
    editor.on("selectionUpdate", handleUpdate);

    return () => {
      editor.off("selectionUpdate", handleUpdate);
    };
  }, [editor]);

  // Close bubble menu when clicking outside or using keyboard navigation
  useEffect(() => {
    if (!editor || !activeSuggestion) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the bubble menu or editor
      if (
        bubbleMenuRef.current?.contains(target) ||
        editor.view.dom.contains(target)
      ) {
        return;
      }

      setActiveSuggestion(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // OPT+Arrow: timeline item navigation
      // Escape: dismiss the menu
      if (
        (event.altKey &&
          (event.key === "ArrowDown" || event.key === "ArrowUp")) ||
        event.key === "Escape"
      ) {
        setActiveSuggestion(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor, activeSuggestion]);

  const handleAccept = useCallback(() => {
    if (!activeSuggestion || !activeSuggestion.isValid) return;

    // Use the mark's acceptSuggestion command to delete the highlighted text
    editor?.commands.acceptSuggestion(activeSuggestion.suggestionId);

    onAccept(activeSuggestion.suggestionId);
  }, [editor, activeSuggestion, onAccept]);

  const handleReject = useCallback(() => {
    if (!activeSuggestion) return;

    // Use the mark's rejectSuggestion command to remove the mark without deleting text
    editor?.commands.rejectSuggestion(activeSuggestion.suggestionId);

    onReject(activeSuggestion.suggestionId);
  }, [editor, activeSuggestion, onReject]);

  // Always render BubbleMenu when editor exists - TipTap needs it mounted
  // to manage visibility via shouldShow. The content is conditionally rendered inside.
  if (!editor) return null;

  // Append to document.body for default variant to avoid clipping issues
  const appendTo = variant === "default" ? document.body : undefined;

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor }) => editor.isActive("suggestionHighlight")}
      options={{
        placement: "top",
        offset: 8,
        flip: { crossAxis: false, fallbackPlacements: ["bottom"] },
      }}
      className="indigo-suggestion-bubble-menu"
      pluginKey={SUGGESTION_BUBBLE_MENU_KEY}
      ref={bubbleMenuRef}
      appendTo={appendTo}
    >
      {activeSuggestion && (
        <SuggestionPopoverContent
          variant={variant}
          badgeLabel="Suggested Removal"
          reason={activeSuggestion.reason}
          reasonDetails={activeSuggestion.reasonDetails}
          confidence={activeSuggestion.confidence}
          isValid={activeSuggestion.isValid}
          onAccept={handleAccept}
          onReject={handleReject}
          onViewRelatedSegment={(() => {
            const relatedId = activeSuggestion.relatedSegmentID;
            return relatedId && onNavigateToSegment
              ? () => onNavigateToSegment(relatedId)
              : undefined;
          })()}
        />
      )}
    </BubbleMenu>
  );
};
